import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface TrendsPageProps {
  embedded?: boolean;
}

const stats = [
  {
    figure: '46%',
    label: 'Rise in hotline complaints',
    detail: 'Kentucky, 2022–2024',
    source: 'KY Youth Advocates',
    url: 'https://kyyouth.org/abuse-in-child-care-programs-blog/?utm_source=chatgpt.com',
  },
  {
    figure: '73%',
    label: 'Rise in substantiated abuse incidents',
    detail: 'Kentucky, same period',
    source: 'KY Youth Advocates',
    url: 'https://kyyouth.org/abuse-in-child-care-programs-blog/?utm_source=chatgpt.com',
  },
  {
    figure: '2,000+',
    label: 'Daycare providers reported for abuse or neglect',
    detail: 'US nationally, in a single year',
    source: 'W. Turley & Associates',
    url: 'https://www.wturley.com/daycare-abuse-statistics/?utm_source=chatgpt.com',
  },
];

const reasons = [
  {
    num: '1',
    title: 'Staffing Pressure & Low Wages',
    points: [
      'Caregiver-to-child ratios are often maxed out to boost revenue — ratios of 1:10 or more are common, increasing stress and reducing oversight.',
      'Low pay (~$14/hr average) and minimal benefits drive high staff turnover and inexperience in the classroom.',
    ],
  },
  {
    num: '2',
    title: 'Training & Oversight Deficiencies',
    points: [
      'New, under-trained staff struggle without sufficient mentorship or classroom management skills.',
      'Many facilities lack standardized training on handling challenging behaviors or children with special needs.',
    ],
  },
  {
    num: '3',
    title: 'Stress, Isolation & Organizational Culture',
    points: [
      'The intersection of high stress, physical isolation, and weak organizational policy is a known risk factor for institutional abuse.',
      'Staff fear reporting peers — especially in smaller or financially vulnerable facilities.',
    ],
  },
  {
    num: '4',
    title: 'Lack of Surveillance & Reporting',
    points: [
      'Most facilities have cameras that record — but no system that watches, understands, or alerts.',
      'Footage is reviewed only after a complaint is filed. The gap between incident and discovery is where harm compounds.',
    ],
  },
];

const TrendsPage: React.FC<TrendsPageProps> = ({ embedded = false }) => {
  const content = (
    <Box sx={{ py: embedded ? 0 : 4 }}>
      {/* Hero */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,159,67,0.08)', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
          <TrendingUpIcon sx={{ color: '#ff9f43', fontSize: 14 }} />
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#ff9f43', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Industry Data
          </Typography>
        </Box>
        <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 2, lineHeight: 1.1 }}>
          Behind closed doors:<br />what the statistics reveal.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, maxWidth: 560, mx: 'auto' }}>
          Abuse in childcare is a growing and under-reported crisis. The numbers are rising across the US and internationally — and the visibility gap is the root cause.
        </Typography>
      </Box>

      {/* Stat cards */}
      <Stack spacing={0} sx={{ mb: 6 }}>
        {stats.map(({ figure, label, detail, source, url }, i) => (
          <Box key={figure} sx={{
            p: { xs: 3, md: 4 },
            bgcolor: '#13131f',
            border: '1px solid rgba(255,159,67,0.12)',
            borderBottom: i < stats.length - 1 ? 'none' : '1px solid rgba(255,159,67,0.12)',
            borderRadius: i === 0 ? '12px 12px 0 0' : i === stats.length - 1 ? '0 0 12px 12px' : 0,
            display: 'flex',
            gap: { xs: 2, md: 4 },
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
          }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.5rem', md: '3rem' }, color: '#ff9f43', lineHeight: 1, flexShrink: 0, minWidth: { md: 140 } }}>
              {figure}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 0.5 }}>
                {label}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>
                {detail}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.5)', flexShrink: 0 }}>
              Source:{' '}
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(148,153,176,0.7)', textDecoration: 'underline' }}>
                {source}
              </a>
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Image */}
      <Box sx={{ mb: 6, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(92,225,230,0.15)' }}>
        <img
          src="/daycare-woman-child.png"
          alt="Childcare statistics"
          style={{ width: '100%', maxHeight: 400, objectFit: 'cover', display: 'block', filter: 'grayscale(0.15) contrast(1.05)' }}
        />
      </Box>

      {/* Why is this happening */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 5 }}>
          Why is this happening?
        </Typography>
        <Stack spacing={2.5}>
          {reasons.map(({ num, title, points }) => (
            <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, alignItems: 'flex-start' }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(255,159,67,0.08)', border: '1px solid rgba(255,159,67,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 14, color: '#ff9f43' }}>{num}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 1.5 }}>
                  {title}
                </Typography>
                <Stack spacing={1}>
                  {points.map((p, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9499b0', mt: 0.8, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>{p}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Closing */}
      <Box sx={{ mt: 5, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, borderLeft: '3px solid #5ce1e6' }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 14, md: 15 }, color: '#9499b0', lineHeight: 1.8 }}>
          Addressing this crisis requires investment in staffing, standardized training, stronger oversight, mandatory surveillance, and robust reporting systems — backed by public funding and policy reform. And in the meantime, it requires technology that can see what no human reviewer ever could: the slow, accumulating pattern of harm before the incident that makes the news.
        </Typography>
      </Box>
    </Box>
  );

  if (embedded) return content;
  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh', py: 8, px: 3 }}>
      <Container maxWidth="md">{content}</Container>
    </Box>
  );
};

export default TrendsPage;
